import { duration } from '@play-co/components';
import StateObserver, { user } from 'src/StateObserver';
import { useCache } from 'src/lib/utils';
import type { BuddiesPlayerMap } from 'src/replicant/buddies/buddies.asyncGetters';
import { getBuruFriends } from 'src/replicant/friends/friends.getters';
import { getUnsetPublicProfileName } from 'src/replicant/profile/profile.getters';
import type { Locale } from 'src/replicant/ruleset/locale';

/** djb2 hash — fast, non-cryptographic, returns a compact base-36 string. */
function simpleHash(str: string): string {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash + str.charCodeAt(i)) & 0xffffffff;
  }
  return (hash >>> 0).toString(36);
}

/**
 * Fetch player data for the given IDs, filling in a name for players who have no
 * public profile.
 *
 * The async getter returns the name raw, so that it doesn't have to load the caller's
 * own state just to pick this fallback - apply it here instead, where the user's
 * locale is already at hand. A player only ever has an empty name when they have no
 * public profile, since setting one requires at least one character.
 */
export async function buddiesFetchPlayers(
  ids: string[],
): Promise<BuddiesPlayerMap> {
  const players =
    await StateObserver.replicant.asyncGetters.buddiesGetPlayers(ids);

  const resolved: BuddiesPlayerMap = {};
  for (const id in players) {
    const player = players[id];
    resolved[id] = {
      ...player,
      name:
        player.name ||
        getUnsetPublicProfileName(user().settings.locale as Locale),
    };
  }

  return resolved;
}

export function buddiesGetPlayers(ids: string[]) {
  const sortedIds = Array.from(new Set(ids)).sort();
  const cacheKey = `buddiesGetPlayers_${simpleHash(sortedIds.join(','))}`;

  return useCache<BuddiesPlayerMap>(
    async () => buddiesFetchPlayers(ids),
    cacheKey,
    duration({ minutes: 10 }),
  );
}

export async function buddiesGetFriends(): Promise<BuddiesPlayerMap> {
  const playersIds = getBuruFriends(user());
  return buddiesGetPlayers(playersIds).get();
}

export function buddiesGetSuggestedPlayers() {
  return useCache(
    async () => {
      return StateObserver.replicant.asyncGetters.buddiesGetSuggestedPlayers();
    },
    'buddiesGetSuggestedPlayers',
    duration({ minutes: 10 }),
  );
}

export function removeBuddiesSuggestionFromCache(playerId: string) {
  buddiesGetSuggestedPlayers().setCached((cached) => {
    if (!cached) return undefined;
    cached.data = cached.data.filter((s) => s.id !== playerId);
    if (cached.data.length === 0) return undefined;
    return cached;
  });
}
